import { useState, useEffect, lazy, Suspense } from 'react'
import { graphql } from 'gatsby'
import Lazyload from 'react-lazy-load'
import Layout from '../components/common/Layout'
import { MetaData } from '../components/common/meta'
import { LayoutMeta } from '../components/common/meta'
import { Trans, useTranslation } from 'gatsby-plugin-react-i18next'
const RecentArticles = lazy(() => import('../components/common/RecentArticles'))
import jsonLDData from '../utils/jsonLDDataHome.js'
import { getLocaleGroup, LOCALE_TO_HREFLANG } from '../utils/localeUrlMap'
import { StaticImage } from 'gatsby-plugin-image'
const HomepageTestimonialCrousal = lazy(() => import("../components/common/carousel/HomepageTestimonialCrousal"))
const HomePageVerticalTabs = lazy(() => import("../components/HomePageVerticalTabs"))
const MobileTab = lazy(() => import('../components/common/MobileTab/index.js'))

// import Testimonials from "../components/common/TestimonialsHome";
/**
 * Main index page (home page)
 *
 * Loads all posts from Ghost and uses pagination to navigate through them.
 * The number of posts that should appear per page can be setup
 * in /utils/siteConfig.js under `postsPerPage`.
 *
 */

export function Head({ location }) {
  // console.log("location.pathname",location.pathname);

  const pageTitles = {
    '/': 'Vantage Fit- A Global Employee Wellness Platform for Corporates',
    '/fr/': 'Vantage Fit - Une plateforme mondiale de bien-être pour les entreprises',
    '/es/': 'Vantage Fit - Una plataforma global de bienestar para empresas',
    '/de/': 'Vantage Fit - Eine globale Mitarbeiter-Wellness-Plattform für Unternehmen',
  }

  const title = pageTitles[location.pathname] || 'Welcome to Vantage Fit - Global Wellness Platform'
  const localeGroup = getLocaleGroup(location.pathname)
  const siteUrl = 'https://www.vantagefit.io'

  return (
    <>
      <title>{title}</title>
      {localeGroup &&
        Object.entries(localeGroup).map(([locale, path]) => (
          <link
            key={locale}
            rel="alternate"
            hrefLang={LOCALE_TO_HREFLANG[locale]}
            href={`${siteUrl}${path}`}
          />
        ))}
      {localeGroup?.['en-US'] && (
        <link rel="alternate" hrefLang="x-default" href={`${siteUrl}${localeGroup['en-US']}`} />
      )}
      <link key="preconnect-cloudinary" rel="preconnect" href="https://res.cloudinary.com" crossOrigin="anonymous" />
      <link
        rel="preload"
        as="image"
        href="https://res.cloudinary.com/vantagecircle/image/upload/f_auto,q_auto,w_650/v1765974729/VantageFit/2025/webp-images/homepage/tinified/Group_1407_1_1"
        imageSrcSet="https://res.cloudinary.com/vantagecircle/image/upload/f_auto,q_auto,w_600/v1765974729/VantageFit/2025/webp-images/homepage/tinified/Group_1407_1_1 600w, https://res.cloudinary.com/vantagecircle/image/upload/f_auto,q_auto,w_650/v1765974729/VantageFit/2025/webp-images/homepage/tinified/Group_1407_1_1 650w, https://res.cloudinary.com/vantagecircle/image/upload/f_auto,q_auto,w_1300/v1765974729/VantageFit/2025/webp-images/homepage/tinified/Group_1407_1_1 1300w"
        imageSizes="(max-width: 768px) 600px, 650px"
        fetchPriority="high"
      />

      <LayoutMeta />
      <MetaData location={location} />
      {location.pathname === `/` && (
        /* Loop over the JSON-LD array and render each script tag */
        // Object.values(jsonLDData).map((data, index) => (
        //     <script key={index} type="application/ld+json">
        //       {JSON.stringify(data)}
        //     </script>
        // ))
        <script type="application/ld+json">{JSON.stringify(jsonLDData)}</script>
      )}
    </>
  )
}

const Index = ({ location }) => {
  const videoUrls = [
    'https://www.youtube.com/embed/LSX4pxSB6Qw',
    'https://www.youtube.com/embed/4h9eRm4hNe0',
    'https://www.youtube.com/embed/L_39IykL-O4',
  ]
  const { t } = useTranslation()
  const [isMobile, setIsMobile] = useState(false)
  const [ismobileTab, setIsMobileTab] = useState(null)
  const [modal, setModal] = useState(false)
  const toggle = () => setModal(!modal)
  const externalCloseBtn = (
    <button
      type="button"
      className="close videoclose"
      style={{ top: '10px', right: '10px', marginRight: '0' }}
      onClick={toggle}
    >
      <svg className="crossico" width="20" height="20" viewBox="0 0 17 17">
        <path
          className="fill_path"
          d="M16.293 1.592l-1.3-1.3-6.7 6.701-6.7-6.7-1.3 1.299 6.7 6.7-6.7 6.701 1.3 1.3 6.7-6.7 6.7 6.7 1.3-1.3-6.7-6.7z"
        ></path>{' '}
      </svg>
    </button>
  )
  useEffect(() => {
    if (typeof window === 'undefined') return

    const mobileQuery = window.matchMedia('(max-width: 768px)')
    const tabletQuery = window.matchMedia('(max-width: 1024px)')

    const update = () => {
      setIsMobile(mobileQuery.matches)
      setIsMobileTab(tabletQuery.matches)
    }

    update()

    mobileQuery.addEventListener('change', update)
    tabletQuery.addEventListener('change', update)

    return () => {
      mobileQuery.removeEventListener('change', update)
      tabletQuery.removeEventListener('change', update)
    }
  }, [])

  const tabs = [
    {
      id: 1,
      title: t('Wellness Programs  <br/> & Challenges'),
      heading: t('Wellness Programs & Challenges'),
      body: t(
        'Run customizable challenges like step challenge, multi-activity challenge, team challenge, virtual marathons, or remote-team initiatives. Flexible formats keep participation high and make wellness inclusive.'
      ),
      imageSrc: '/v1765279507/VantageFit/2025/27221f31ebbd034a8cf30a1b875f2453de40c2da',
      imageAlt: 'Feedback tags illustration',
      btnbg: 'linear-gradient(180deg, #FF917A 0%, #FF9D57 100%)',
      borderColor: '#FF9D57',
      logo: 'https://res.cloudinary.com/vantagecircle/image/upload/f_auto,q_auto/v1765346794/VantageFit/2025/Vector_1',
      bgImg: '/v1767855165/VantageFit/2026/Wellness_Programs_Challenges',
      url: '/multi-activity-challenges/',
    },
    {
      id: 2,
      title: t('Incentivization & <br/> Rewards'),
      heading: t('Incentivization & Rewards'),
      body: t(
        'Strengthen healthy habits with points, badges, and redeemable rewards directly tied to effort and progress. This makes motivation continuous, not forced.'
      ),
      imageSrc: '/v1765279507/VantageFit/2025/27221f31ebbd034a8cf30a1b875f2453de40c2da',
      imageAlt: 'Recurring voice patterns illustration',
      btnbg: 'linear-gradient(152.82deg, rgba(52, 128, 194, 0.25) 0.55%, #41D8B4 84.59%)',
      borderColor: '#41D8B4',
      logo: 'https://res.cloudinary.com/vantagecircle/image/upload/f_auto,q_auto/v1701412164/VantageFit/2023/Incentivization_Rewards',
      bgImg: '/v1767855165/VantageFit/2026/Incentivization_Rewards',
      url: '/wellness-rewards-program/',
    },
    {
      id: 3,
      title: t('Workforce  <br/> Health Analytics'),
      heading: t('Workforce Health Analytics'),
      body: t(
        'Access consolidated wellness insights across participation, health patterns, and engagement levels. Get actionable insights to assess program success.'
      ),
      imageSrc: '/v1765279507/VantageFit/2025/27221f31ebbd034a8cf30a1b875f2453de40c2da',
      imageAlt: 'Sentiment analysis illustration',
      btnbg: 'linear-gradient(103.86deg, #2752A1 1.93%, rgba(111, 118, 255, 0.84) 127.12%)',
      borderColor: '#274CA9',
      logo: 'https://res.cloudinary.com/vantagecircle/image/upload/f_auto,q_auto/v1699360829/VantageFit/2023/Health_analytics',
      bgImg: '/v1767855165/VantageFit/2026/Workforce_Health_Analytics',
      url: '/health-fitness-analytics/',
    },
    {
      id: 4,
      title: t('Personalised <br/> Wellness Plans'),
      heading: t('Personalised Wellness Plans'),
      body: t(
        'Provide personalized fitness, nutrition, and mental-wellbeing plans, alongside guided sessions and recommendations.'
      ),
      imageSrc: '/v1765279507/VantageFit/2025/27221f31ebbd034a8cf30a1b875f2453de40c2da',
      imageAlt: 'Predictive analysis illustration',
      btnbg: 'linear-gradient(113.34deg, #FFBD7F -20.8%, #F15162 77.68%)',
      borderColor: '#F15162 ',
      logo: 'https://res.cloudinary.com/vantagecircle/image/upload/f_auto,q_auto/v1765280992/VantageFit/2025/Icon',
      bgImg: '/v1767855165/VantageFit/2026/Personalised_Wellness_Plans',
      url: '/features/personalized-programs/',
    },
    {
      id: 5,
      title: t('Wellness <br/> Engagement Hub'),
      heading: t('Wellness Engagement Hub'),
      body: t(
        'Combine community chat, focused groups, social feeds, and expert-crafted wellness challenges to drive lasting employee engagement.'
      ),
      imageSrc: '/v1765279507/VantageFit/2025/27221f31ebbd034a8cf30a1b875f2453de40c2da',
      imageAlt: 'Parallel surveys dashboard illustration',
      btnbg: 'linear-gradient(106.48deg, #89E050 -6.59%, #5C9B59 97.72%)',
      borderColor: '#248638 ',
      logo: 'https://res.cloudinary.com/vantagecircle/image/upload/f_auto,q_auto/v1699360829/VantageFit/2023/Holistic',
      bgImg: '/v1767855165/VantageFit/2026/Wellness_Engagement_Hub',
      url: '/features/engagement-tools/',
    },
  ]

  return (
    <>
      <Layout isHome={true}>
        {location.pathname === `/` ||
          location.pathname === `/fr/` ||
          location.pathname === `/es/` ||
          location.pathname === `/de/` ? (
          // <Topthree id="1" />
          <div>
            <section name="home" className="homepage hero-bg hero-bg-25 position-relative section-pdn-hero">
              <div className="container custom_container d-flex justify-content-start align-items-center p0 m-d-block heroimg">
                <div className="col-12 col-md-6">
                  <div className="col-12 px-0">
                    <h1 className="font-weight-semibold hero-heading pt-0 no-br">
                      <Trans>
                        A Wellness Platform <br /> Employees Love to Use
                      </Trans>
                    </h1>
                    <p className="hero-para font-weight-semibold no-br mb-0">
                      <Trans>
                        Designed to drive real participation through challenges, rewards, and experiences employees want
                        to be part of.
                      </Trans>
                    </p>
                    <div className="row" style={{ margin: '47px 0' }}>
                      <div className="col-12 col-md-6 px-md-0 d-flex align-items-center justify-content-start">
                        <StaticImage
                          src="https://res.cloudinary.com/vantagecircle/image/upload/f_auto,q_auto,w_313/v1765949390/VantageFit/2025/webp-images/homepage/Group_1354_1"
                          alt="crowd review logo"
                          placeholder="none"
                          layout="constrained"
                          width={510}
                          height={83}
                          imgStyle={{ objectFit: 'contain' }}
                          fetchPriority="high"
                        />
                      </div>
                    </div>
                    <div className="pl-0 pr-0 col-12 d-md-flex justify-content-md-center d-lg-flex justify-content-lg-start align-items-center">
                      <a
                        id="demo_btn_hero"
                        href={
                          location.pathname === '/fr/'
                            ? '/fr/planifier-demo/'
                            : location.pathname === '/es/'
                              ? '/es/solicita-una-demo/'
                              : location.pathname === '/de/'
                                ? '/de/demo-anfordern/'
                                : '/request-demo/'
                        }
                        className="vfitcompanycta mr-3 arrowbtn-img"
                      >
                        <Trans>Book a Demo</Trans>
                      </a>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 mt-5 mt-md-0">
                  <div className="right-img tr-right">
                    <picture>
                      <source
                        media="(max-width: 768px)"
                        srcSet={t(
                          `https://res.cloudinary.com/vantagecircle/image/upload/f_auto,q_auto,w_600/v1765974729/VantageFit/2025/webp-images/homepage/tinified/Group_1407_1_1`)}
                      />
                      <source
                        media="(min-width: 769px)"
                        srcSet={`${t('https://res.cloudinary.com/vantagecircle/image/upload/f_auto,q_auto,w_650/v1765974729/VantageFit/2025/webp-images/homepage/tinified/Group_1407_1_1')} 1x, ${t('https://res.cloudinary.com/vantagecircle/image/upload/f_auto,q_auto,w_1300/v1765974729/VantageFit/2025/webp-images/homepage/tinified/Group_1407_1_1')} 2x`}
                      />
                      <img
                        src={t(
                          `https://res.cloudinary.com/vantagecircle/image/upload/f_auto,q_auto,w_600/v1765974729/VantageFit/2025/webp-images/homepage/tinified/Group_1407_1_1`)}
                        width="833"
                        height="414"
                        alt="Image for showing the Vantage Fit App"
                        fetchPriority="high"
                        style={{ width: '100%', height: 'auto' }}
                      />
                    </picture>
                  </div>
                </div>
              </div>
            </section>
            <section className="position-relative home-bg-1 section-pdn-1 abtest-rempadn pt-4">
              <div className="container custom_container">
                <div className="recog-logos vfit-hm-comp-logos">
                  <h2 className="py-4 text-white section-heading">
                    <Trans>Improving wellness across global organizations</Trans>
                  </h2>
                  <div className="d-flex justify-content-center strip-logo-container"></div>
                </div>
                <h2 className="section-heading text-center mb-5 pb-5 pb-md-0 text-white green-text">
                  <Trans>How Corporate Wellness programs help?</Trans>
                </h2>
                <div className="col-12 col-md-10 mx-auto px-0">
                  <div className="row">
                    <div className="col-12 col-md-4 mb-4 mb-md-0 d-flex justify-content-center">
                      <div className="focus-sect wellness-group">
                        <div className="w-100 h-50 position-relative well-gh-img">
                          <img
                loading="lazy"
                            src="https://res.cloudinary.com/vantagecircle/image/upload/f_auto,q_auto/v1765452527/VantageFit/2025/webp-images/homepage/b1"
                            width={120}
                            height={120}
                            alt="Motivation icon"
                            style={{ display: 'block' }}
                          />
                        </div>
                        <div className="row h-50 well-gh-content">
                          <div className="col-4 px-0 d-flex align-items-end pb-md-2">
                            <a
                              href="https://www.shrm.org/in/topics-tools/news/benefits-compensation/wellness-programs-employee-retention-tool"
                              className=""
                              target="_blank"
                              style={{
                                fontSize: '11px',
                                color: 'rgba(255, 255, 255, 0.5)',
                                textDecoration: 'underline',
                              }}
                            >
                              <i>
                                <Trans>source</Trans>
                              </i>
                            </a>
                          </div>
                          <div className="col-8">
                            <div
                              className="d-flex flex-column align-items-start justify-content-between h-100"
                              style={{ paddingLeft: 30 }}
                            >
                              <p className="text-white m-0" style={{ fontSize: 68, lineHeight: 1.2, fontWeight: 300 }}>
                                40%
                              </p>
                              <p className="text-white text-left section-para m-0" style={{ maxWidth: 137 }}>
                                <Trans>
                                  more motivated <br /> employees
                                </Trans>
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-4 mb-4 mb-md-0 d-flex justify-content-center">
                      <div className="focus-sect wellness-group">
                        <div className="w-100 h-50 position-relative well-gh-img">
                          <img
                loading="lazy"
                            src="https://res.cloudinary.com/vantagecircle/image/upload/f_auto,q_auto/v1765452526/VantageFit/2025/webp-images/homepage/b2"
                            width={77}
                            height={153}
                            alt="Motivation icon"
                            style={{ display: 'block', top: -30 }}
                            className="s-2"
                          />
                        </div>
                        <div className="row h-50 well-gh-content">
                          <div className="col-4 px-0 d-flex align-items-end pb-2">
                            <a
                              href="https://www.shrm.org/in/topics-tools/news/benefits-compensation/wellness-programs-employee-retention-tool"
                              className=""
                              target="_blank"
                              style={{
                                fontSize: '11px',
                                color: 'rgba(255, 255, 255, 0.5)',
                                textDecoration: 'underline',
                              }}
                            >
                              <i>
                                <Trans>source</Trans>
                              </i>
                            </a>
                          </div>
                          <div className="col-8">
                            <div className="d-flex flex-column align-items-start justify-content-between h-100 pl-4">
                              <p className="text-white m-0" style={{ fontSize: 68, lineHeight: 1.2, fontWeight: 300 }}>
                                26%
                              </p>
                              <p className="text-white text-left section-para m-0">
                                <Trans>
                                  drop in <br /> absentiseem
                                </Trans>
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-4 mb-4 mb-md-0 d-flex justify-content-center">
                      <div className="focus-sect wellness-group">
                        <div className="w-100 h-50 position-relative well-gh-img">
                          <img
                loading="lazy"
                            src="https://res.cloudinary.com/vantagecircle/image/upload/f_auto,q_auto/v1765452525/VantageFit/2025/webp-images/homepage/b3"
                            width={120}
                            height={120}
                            alt="Motivation icon"
                            style={{ display: 'block' }}
                            className="s-3"
                          />
                        </div>
                        <div className="row h-50 well-gh-content">
                          <div className="col-4 px-0 d-flex align-items-end pb-2">
                            <a
                              href="https://www.healthaffairs.org/doi/abs/10.1377/hlthaff.2009.0626"
                              target="_blank"
                              className=""
                              style={{
                                fontSize: '11px',
                                color: 'rgba(255, 255, 255, 0.5)',
                                textDecoration: 'underline',
                              }}
                            >
                              <i>
                                <Trans>source</Trans>
                              </i>
                            </a>
                          </div>
                          <div className="col-8 px-0">
                            <div
                              className="d-flex flex-column align-items-start justify-content-between h-100"
                              style={{ paddingLeft: 10 }}
                            >
                              <p className="text-white m-0" style={{ fontSize: 68, lineHeight: 1.2, fontWeight: 300 }}>
                                $3.27
                              </p>
                              <p className="text-white section-para m-0">
                                <Trans>
                                  medical cost savings <br /> per 1$ invested
                                </Trans>
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="position-relative section-pdn home-bg-3 pb-5 pt-5">
                  <div className="container custom_container pt-5">
                    <h2 className="section-heading text-center mb-5 text-white pb-5 pb-md-0">
                      <Trans>A platform based on Cue > Action > Reward</Trans>
                    </h2>
                    <div className="col-12 col-md-11 mx-auto px-0 text-white text-center">
                      <div className="row">
                        <div className="col-12 col-sm-12 col-md-4 d-flex justify-content-center align-items-center">
                          <img
                            className="img-responsive h-100"
                            loading="lazy"
                            src={t(
                              `https://res.cloudinary.com/vantagecircle/image/upload/f_auto,q_auto,w_322,h_418,c_scale/v1711542464/VantageFit/2023/CUE-1`
                            )}
                            alt="Image for Cue"
                            width="322"
                            height="418"
                          />
                        </div>
                        <div className="col-12 col-sm-12 col-md-4 d-flex justify-content-center align-items-center">
                          <img
                            className="img-responsive h-100"
                            loading="lazy"
                            src={t(
                              `https://res.cloudinary.com/vantagecircle/image/upload/f_auto,q_auto,w_322,h_418,c_scale/v1711542464/VantageFit/2023/ACTION-1`
                            )}
                            alt="Image for Action"
                            width="322"
                            height="418"
                          />
                        </div>
                        <div className="col-12 col-sm-12 col-md-4 d-flex justify-content-center align-items-center">
                          <img
                            className="img-responsive h-100"
                            loading="lazy"
                            src={t(
                              `https://res.cloudinary.com/vantagecircle/image/upload/f_auto,q_auto,w_322,h_418,c_scale/v1711542467/VantageFit/2023/REWARD-1`
                            )}
                            alt="Image for Reward"
                            width="322"
                            height="418"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            <section className="w-100 section-pdn-1 px-2" style={{ background: '#F8F8F8' }}>
              <h2 className="section-heading text-center mb-5 px-4 pb-5 pb-md-0">
                <Trans>A happier and healthier workforce, holistically</Trans>
              </h2>
              {ismobileTab === null ? (
                <div style={{ minHeight: 730 }} />
              ) : ismobileTab ? (
                <Lazyload offset={200}>
                  <Suspense fallback={null}>
                    <MobileTab tabs={tabs} />
                  </Suspense>
                </Lazyload>
              ) : (
                <Lazyload offset={200}>
                  <Suspense fallback={null}>
                    <HomePageVerticalTabs tabs={tabs} />
                  </Suspense>
                </Lazyload>
              )}
              <div className="container custom_container" style={{ marginTop: '44px' }}>
                <div
                  className="pl-0 pr-0 col-12 mtb25 d-md-flex justify-content-md-center d-lg-flex justify-content-lg-center align-items-center"
                  style={{ marginBottom: '13px' }}
                >
                  <a
                    id="demo_btn_hero"
                    href={
                      location.pathname === '/fr/'
                        ? '/fr/planifier-demo/'
                        : location.pathname === '/es/'
                          ? '/es/solicita-una-demo/'
                          : location.pathname === '/de/'
                            ? '/de/demo-anfordern/'
                            : '/request-demo/'
                    }
                    className="vfitcompanycta mr-lg-3 arrowbtn-img mb-0"
                  >
                    <Trans>Book a Demo</Trans>
                  </a>
                </div>
              </div>
            </section>
            <section
              className=""
              style={{
                background: 'linear-gradient(180deg, rgba(248, 248, 248, 1) 30%, rgba(255, 255, 255, 1) 0%)',
              }}
            >
              <div className="container custom_container">
                <div
                  className="hm-pg prime-vf-page client-testimonial"
                  style={{ background: 'white', marginBottom: '75px' }}
                >
                  <h2 className="pb-2 mb-0 section-heading text-center no-br" style={{ paddingTop: 39 }}>
                    <Trans>Real people, real results</Trans>
                  </h2>
                  <div className="pb-2">
                    <Lazyload offset={200}>
                      <Suspense fallback={null}>
                        <HomepageTestimonialCrousal />
                      </Suspense>
                    </Lazyload>
                  </div>
                </div>
              </div>
            </section>
            <section className="position-relative section-pdn-1 bg-dark-vf100">
              <div className="d-flex flex-column m-d-block justify-content-between align-items-center container custom_container p0">
                <div className="col-12">
                  <div className="col-12">
                    <h2 className="font-weight-light text-white py-2 text-center section-heading">
                      <Trans>Why Vantage Fit?</Trans>
                    </h2>
                    <div className="row my-4">
                      <div className="col-12 col-md-4 d-flex my-2 my-md-0">
                        <div className="group-elpsis">
                          <svg
                            width="28"
                            height="28"
                            viewBox="0 0 28 28"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ width: 28, height: 28 }}
                          >
                            <path
                              d="M3.71875 22.3125C3.71875 22.4865 3.78789 22.6535 3.91096 22.7765C4.03403 22.8996 4.20095 22.9688 4.375 22.9688H5.85156C5.99792 22.9688 6.14007 22.9198 6.25543 22.8298C6.37079 22.7397 6.45273 22.6137 6.48823 22.4717L7.51237 18.375H3.71875V22.3125Z"
                              fill="white"
                            ></path>
                            <path
                              d="M8.09375 17.7781C10.2564 18.1073 15.0176 19.8054 18.1562 22.6862V5.31384C15.0176 8.19467 10.2564 9.89272 8.09375 10.222V17.7781Z"
                              fill="white"
                            ></path>
                            <path
                              d="M20.5625 2.625C20.0985 2.62552 19.6537 2.81006 19.3256 3.13814C18.9976 3.46621 18.813 3.91103 18.8125 4.375V23.625C18.8125 24.0891 18.9969 24.5342 19.3251 24.8624C19.6533 25.1906 20.0984 25.375 20.5625 25.375C21.0266 25.375 21.4717 25.1906 21.7999 24.8624C22.1281 24.5342 22.3125 24.0891 22.3125 23.625V4.375C22.312 3.91103 22.1274 3.46621 21.7994 3.13814C21.4713 2.81006 21.0265 2.62552 20.5625 2.625Z"
                              fill="#41D8B4"
                            ></path>
                            <path d="M3.71875 10.2812H7.4375V17.7188H3.71875V10.2812Z" fill="#41D8B4"></path>
                            <path
                              d="M0.21875 12.25V15.75C0.219329 16.272 0.426936 16.7724 0.796023 17.1415C1.16511 17.5106 1.66553 17.7182 2.1875 17.7188H3.0625V10.2812H2.1875C1.66553 10.2818 1.16511 10.4894 0.796023 10.8585C0.426936 11.2276 0.219329 11.728 0.21875 12.25Z"
                              fill="white"
                            ></path>
                            <path
                              d="M27.125 13.3438H24.9375C24.7635 13.3438 24.5965 13.4129 24.4735 13.536C24.3504 13.659 24.2812 13.826 24.2812 14C24.2812 14.174 24.3504 14.341 24.4735 14.464C24.5965 14.5871 24.7635 14.6562 24.9375 14.6562H27.125C27.299 14.6562 27.466 14.5871 27.589 14.464C27.7121 14.341 27.7812 14.174 27.7812 14C27.7812 13.826 27.7121 13.659 27.589 13.536C27.466 13.4129 27.299 13.3438 27.125 13.3438Z"
                              fill="#41D8B4"
                            ></path>
                            <path
                              d="M23.8579 9.91682C23.9563 9.91682 24.0533 9.89461 24.1418 9.85185L26.1135 8.90396C26.2703 8.82853 26.3908 8.69389 26.4484 8.52964C26.506 8.36539 26.496 8.185 26.4205 8.02814C26.3451 7.87127 26.2105 7.7508 26.0462 7.69321C25.882 7.63562 25.7016 7.64564 25.5447 7.72106L23.5733 8.66902C23.4392 8.73344 23.3309 8.84152 23.2663 8.97555C23.2017 9.10957 23.1845 9.26158 23.2176 9.40663C23.2507 9.55169 23.3322 9.68119 23.4486 9.77389C23.5649 9.86659 23.7094 9.91699 23.8582 9.91682H23.8579Z"
                              fill="#41D8B4"
                            ></path>
                            <path
                              d="M26.1131 19.096L24.1417 18.1481C23.985 18.0741 23.8054 18.065 23.6421 18.1229C23.4788 18.1808 23.345 18.3009 23.2699 18.4571C23.1948 18.6132 23.1845 18.7928 23.2413 18.9565C23.2981 19.1202 23.4173 19.2548 23.573 19.3309L25.5444 20.2789C25.7012 20.3543 25.8816 20.3643 26.0459 20.3068C26.2101 20.2492 26.3448 20.1287 26.4202 19.9718C26.4956 19.815 26.5056 19.6346 26.4481 19.4703C26.3905 19.3061 26.27 19.1714 26.1131 19.096Z"
                              fill="#41D8B4"
                            ></path>
                          </svg>
                        </div>
                        <p className="text-white section-para font-weight-normal ml-3 mb-0" style={{ maxWidth: 255 }}>
                          <Trans>Spark health awareness across your workforce</Trans>
                        </p>
                      </div>
                      <div className="col-12 col-md-4 d-flex  my-2 my-md-0">
                        <div className="group-elpsis">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={28}
                            height={28}
                            viewBox="0 0 28 28"
                            fill="none"
                            style={{ width: 28, height: 28 }}
                          >
                            <g clipPath="url(#clip0_6472_1242)">
                              <path
                                d="M3 7.18616V21.271C1.27523 21.0089 -9.39369e-05 20.1062 -9.39369e-05 19.0356V9.42146C-9.39369e-05 8.35084 1.27523 7.44817 3 7.18616Z"
                                fill="#41D8B4"
                              />
                              <path
                                d="M5.75702 5.32117H6.29892C7.57396 5.32117 8.61133 6.35853 8.61133 7.63352V20.8237C8.61133 22.0987 7.57396 23.136 6.29892 23.136H5.75702C4.48199 23.136 3.44462 22.0987 3.44462 20.8237V7.63352C3.44467 6.35848 4.48199 5.32117 5.75702 5.32117Z"
                                fill="white"
                              />
                              <path d="M8.99977 11.366H18.998V17.0084H8.99977V11.366Z" fill="#41D8B4" />
                              <path
                                d="M28 8.96441V18.5785C28 19.6491 26.7247 20.5519 25 20.8139V6.729C26.7247 6.99107 28 7.89379 28 8.96441Z"
                                fill="#41D8B4"
                              />
                              <path
                                d="M21.7024 4.86401H22.2443C23.5193 4.86401 24.5566 5.90138 24.5566 7.17637V20.3664C24.5566 21.6415 23.5193 22.6788 22.2443 22.6788H21.7024C20.4274 22.6788 19.39 21.6414 19.39 20.3664V7.17637C19.3901 5.90133 20.4274 4.86401 21.7024 4.86401Z"
                                fill="white"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_6472_1242">
                                <rect width="28" height="28" fill="white" transform="matrix(-1 0 0 1 28 0)" />
                              </clipPath>
                            </defs>
                          </svg>
                        </div>
                        <p className="text-white section-para font-weight-normal ml-3 mb-0" style={{ maxWidth: 255 }}>
                          <Trans>Equip employees with tools for lasting wellness</Trans>
                        </p>
                      </div>
                      <div className="col-12 col-md-4 d-flex  my-2 my-md-0">
                        <div className="group-elpsis">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={28}
                            height={28}
                            viewBox="0 0 28 28"
                            fill="none"
                            style={{ width: 28, height: 28 }}
                          >
                            <path
                              d="M3.16831 9.15801L2.67827 9.74627C2.3689 10.1176 1.74616 9.99037 1.62445 9.53089L1.43167 8.80308C1.39278 8.65625 1.28043 8.53703 1.13122 8.48426L0.391644 8.22271C-0.0752527 8.0576 -0.140438 7.45488 0.281339 7.2028L0.949446 6.80349C1.08423 6.72294 1.16871 6.58445 1.17536 6.43316L1.20832 5.68326C1.22913 5.20985 1.81159 4.96463 2.19396 5.2683L2.79965 5.74933C2.92184 5.84637 3.08641 5.88001 3.23972 5.83927L3.99967 5.63735C4.47943 5.50988 4.90459 5.96104 4.71913 6.4008L4.42536 7.0974C4.3661 7.23793 4.38332 7.39721 4.47142 7.52332L4.90814 8.14843C5.18384 8.54306 4.86415 9.06711 4.36716 9.03523L3.57991 8.98472C3.42109 8.97454 3.26717 9.03934 3.16831 9.15801Z"
                              fill="white"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M5.76458 2.97228L6.14766 3.26104C6.24503 3.33443 6.2982 3.4487 6.28984 3.5666L6.25695 4.03049C6.2312 4.39364 6.65439 4.62725 6.97308 4.42579L7.38017 4.16845C7.48363 4.10305 7.61431 4.09026 7.72961 4.13426L8.18324 4.30736C8.53837 4.44288 8.90271 4.13221 8.79977 3.78165L8.66827 3.33385C8.63486 3.22003 8.66245 3.09786 8.74207 3.00715L9.05532 2.65025C9.30055 2.37084 9.10252 1.94524 8.72021 1.93003L8.23186 1.91061C8.10774 1.90568 7.99412 1.84296 7.92803 1.7429L7.66799 1.34922C7.46443 1.04102 6.9777 1.08865 6.84436 1.42982L6.67404 1.86562C6.63075 1.97638 6.53293 2.05979 6.41247 2.08866L5.9385 2.20225C5.56746 2.29118 5.46468 2.74622 5.76458 2.97228Z"
                              fill="#41D8B4"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M10.965 8.98364L11.4459 9.33712C11.7849 9.58628 11.6764 10.0971 11.2623 10.2014L10.6748 10.3496C10.5413 10.3832 10.4338 10.4772 10.3873 10.6008L10.1825 11.1452C10.0381 11.5289 9.49394 11.5885 9.26159 11.2462L8.93197 10.7604C8.85708 10.6501 8.7299 10.5819 8.59188 10.578L7.98436 10.5609C7.55614 10.5489 7.32831 10.075 7.59882 9.75899L7.98259 9.31069C8.06978 9.20884 8.0987 9.0727 8.05993 8.94664L7.88926 8.39176C7.76896 8.00064 8.17236 7.64808 8.57189 7.79516L9.1387 8.00382C9.26748 8.05122 9.41253 8.03531 9.52659 7.96128L10.0286 7.63542C10.3825 7.40572 10.8596 7.66172 10.8361 8.06862L10.8026 8.64588C10.795 8.77703 10.8557 8.90334 10.965 8.98364Z"
                              fill="#41D8B4"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M8.08575 13.2306H12.7959V28H8.08575C7.02597 28 6.16803 27.184 6.16803 26.1758V15.0547C6.16803 14.0466 7.02597 13.2306 8.08575 13.2306Z"
                              fill="white"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M13.637 28V13.2306H16.8668V28H13.637Z"
                              fill="#41D8B4"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M23.5787 16.5749L20.1891 13.2306H17.7079V28H22.4096C23.4694 28 24.3273 27.184 24.3273 26.1758V17.079C24.0497 16.9589 23.7974 16.7909 23.5787 16.5749Z"
                              fill="white"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M22.9731 8.35007L27.5823 12.9025C28.1543 13.4706 28.1374 14.3667 27.5403 14.9107L26.287 16.0628C25.6983 16.6069 24.7563 16.5909 24.1843 16.0228L19.5751 11.4704L22.9731 8.35007Z"
                              fill="white"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M20.147 5.56581L16.749 8.68614L18.9863 10.8944L22.3844 7.77403L20.147 5.56581Z"
                              fill="#41D8B4"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M12.8463 0.397286C13.4351 -0.146758 14.3771 -0.130741 14.9491 0.437331L19.5583 4.98977L16.1602 8.10204L11.5511 3.5576C10.9791 2.98958 10.9959 2.0935 11.5847 1.5494L12.8463 0.397286Z"
                              fill="white"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M20.3068 1.64541C19.6256 0.98138 18.6162 1.21338 17.6237 1.92547L19.8527 4.12568C20.2816 3.91766 20.7694 3.79762 21.2657 3.79762C21.2909 3.79762 21.3246 3.79762 21.3498 3.80563C21.1479 2.90154 20.7106 2.03749 20.3068 1.64541Z"
                              fill="#41D8B4"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M20.4751 4.73375C21.2741 4.44568 22.2077 4.61372 22.8385 5.22979C23.4609 5.84582 23.6039 6.74195 23.2675 7.49403L20.4751 4.73375Z"
                              fill="white"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M26.5057 7.76603C26.1104 7.374 25.2273 6.92595 24.2853 6.70191C24.2768 7.18999 24.1422 7.67002 23.8815 8.10205L26.1272 10.3103C26.9011 9.3822 27.1786 8.43812 26.5057 7.76603Z"
                              fill="#41D8B4"
                            />
                          </svg>
                        </div>
                        <p className="text-white section-para font-weight-normal ml-3 mb-0" style={{ maxWidth: 255 }}>
                          <Trans>Reward those who prioritize their well-being</Trans>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-center col-12 position-relative mt-5">
                  <div
                    className="p-4 position-relative"
                    style={{
                      background: '#1D2228',
                      borderRadius: '37px',
                      zIndex: 1,
                      maxWidth: '650px',
                      width: '100%',
                    }}
                  >
                    <Lazyload offset={100}>
                      <div
                        style={{
                          position: 'relative',
                          paddingBottom: '62.31%', // 405/650 = 62.31% (maintains aspect ratio 16:9)
                          height: 0,
                          overflow: 'hidden',
                          borderRadius: '22px',
                          border: '2px solid rgb(255, 255, 255)',
                        }}
                      >
                        <iframe
                          src="https://www.youtube.com/embed/tiIie7XenmE?si=v2-s43Gg83CnahWG"
                          title="YouTube video player"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          referrerPolicy="strict-origin-when-cross-origin"
                          allowFullScreen
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            border: 'none',
                          }}
                        ></iframe>
                      </div>
                    </Lazyload>
                  </div>
                  <div className="green-glow"></div>
                  <div className="blue-glow"></div>
                </div>

                <div
                  className="row mt-5 rounded-lg"
                  style={{
                    background:
                      'linear-gradient(133.25deg, rgba(65, 216, 180, 0.1) 0%, rgba(117, 115, 215, 0.082588) 52.64%, rgba(196, 196, 196, 0.0718695) 97.13%)',
                    backdropFilter: 'blur(50.4900016784668px)',
                    boxShadow: '0px 20px 22px 1px #161C2372',
                    padding: '0.4rem 2rem',
                    border: '1px solid #FFFFFF33',
                  }}
                >
                  <div className="col-12 col-md-6 d-flex align-items-center">
                    <p className="text-white text-center text-md-left mb-0 w-100 mt-3 mt-md-0" style={{ fontSize: 22 }}>
                      <Trans>Your data is safe with us</Trans>
                    </p>
                  </div>
                  <div className="col-12 col-md-6 my-3 my-md-0">
                    <StaticImage
                      src="https://res.cloudinary.com/vantagecircle/image/upload/f_auto,q_auto/v1766414004/VantageFit/2025/Hipaa"
                      alt="safe data logos"
                      loading="lazy"
                      formats={['auto', 'webp']}
                      layout="constrained"
                      placeholder="blurred"
                      width={600}
                      quality={80}
                      transformOptions={{ fit: 'contain' }}
                      style={{ width: '100%', height: '100%' }}
                    />
                  </div>
                </div>
                <div className="d-flex flex-column flex-md-row mx-auto" style={{ marginTop: '27px' }}>
                  <div className="pl-0 pr-0 col-12 col-md-6 mtb25 d-md-flex justify-content-md-center d-lg-flex justify-content-lg-center align-items-center">
                    <a
                      id="demo_btn_hero"
                      href={
                        location.pathname === '/fr/'
                          ? '/fr/planifier-demo/'
                          : location.pathname === '/es/'
                            ? '/es/solicita-una-demo/'
                            : location.pathname === '/de/'
                              ? '/de/demo-anfordern/'
                              : '/request-demo/'
                      }
                      className="vfitcompanycta mr-lg-3 arrowbtn-img mb-0"
                    >
                      <Trans>Book a Demo</Trans>
                    </a>
                  </div>
                  <div className="pl-0 pr-0 col-12 col-md-6 mtb25 d-md-flex justify-content-md-center d-flex justify-content-center align-items-center my-0">
                    <a
                      href={
                        location.pathname === '/fr/'
                          ? '/fr/tarification/'
                          : location.pathname === '/es/'
                            ? '/es/precios/'
                            : location.pathname === '/de/'
                              ? '/de/preise/'
                              : '/pricing/'
                      }
                      className=" mr-md-3 d-flex justify-content-center p-2 p-md-3 vw-price"
                    >
                      <Trans>View Pricing</Trans>
                    </a>
                  </div>
                </div>
              </div>
            </section>
            <section className="section-pdn about-pattern-vfit apv position-relative">
              <h2 className="section-heading section-heading-top text-center no-br mb-2 mt-0">
                <Trans>Our Wellness Blogs</Trans>
              </h2>
              <p className="section-para font-weight-normal text-center no-br">
                <Trans>
                  Gain access to expert insights and reliable information on employee well-being, corporate wellness,
                  and health from our trusted professionals.
                </Trans>
              </p>
              <div className="container custom_container">
                <div className="row hm-blog-card">
                  <div className="col-12 col-md-3">
                    <div className="">
                      <a href={t(`/en/blog/step-challenge/`)}>
                        <img
                          loading="lazy"
                          className="img-responsive"
                          src="https://res.cloudinary.com/vantagecircle/image/upload/w_275/f_auto,q_auto/v1773307619/VantageFit/2026/Step-challenge.webp"
                          alt="Image for How to Design a Winning Step Challenge at Work?"
                          width="279"
                          height="156"
                        />
                        <h3 className="hm-blog-title">
                          <Trans>How to Set Up a Step Challenge at Work?</Trans>
                        </h3>
                        <p className="hm-blog-desc">
                          <Trans>
                            Office step challenges are a powerful workplace wellness tool that employers can implement
                            to promote health and wellness in the workplace.
                          </Trans>
                        </p>
                      </a>
                    </div>
                  </div>
                  <div className="col-12 col-md-9 right-blogs">
                    <div className="row">
                      <div className="col-12 col-md-7">
                        <a href={t(`/en/blog/employee-health/`)}>
                          <div className="row mb-3 mb-md-4 d-lg-flex justify-content-lg-start align-items-center">
                            <div className="col-12 col-md-5 pl-0">
                              <img
                                loading="lazy"
                                className="img-responsive"
                                src={t(`https://res.cloudinary.com/vantagecircle/image/upload/w_185/f_auto,q_auto/v1773307619/VantageFit/2026/Employee-Health.webp`)}
                                alt="Image for Employee Health: A Vital Factor For Workplace Productivity"
                                width="189"
                                height="106"
                              />
                            </div>
                            <div className="col-12 col-md-7">
                              <h3 className="hm-blog-title">
                                <Trans>Employee Health: A Vital Factor For Workplace Productivity</Trans>
                              </h3>
                            </div>
                          </div>
                        </a>
                        <a href={t(`/en/blog/benefits-of-employee-wellness-programs/`)}>
                          <div className="row mb-3 mb-md-4 d-lg-flex justify-content-lg-start align-items-center">
                            <div className="col-12 col-md-5 pl-0">
                              <img
                                loading="lazy"
                                className="img-responsive"
                                src="https://res.cloudinary.com/vantagecircle/image/upload/w_185/f_auto,q_auto/v1773307619/VantageFit/2026/Employee-Wellness-Program-1.webp"
                                alt="Image for Image for Top 10 Benefits Of Employee Wellness Programs"
                                width="189"
                                height="106"
                              />
                            </div>
                            <div className="col-12 col-md-7">
                              <h3 className="hm-blog-title">
                                <Trans>Top 10 Benefits Of Employee Wellness Programs</Trans>
                              </h3>
                            </div>
                          </div>
                        </a>
                        <a href={t(`/en/blog/self-care-at-work/`)}>
                          <div className="row d-lg-flex justify-content-lg-start align-items-center">
                            <div className="col-12 col-md-5 pl-0">
                              <img
                                loading="lazy"
                                className="img-responsive"
                                src="https://res.cloudinary.com/vantagecircle/image/upload/w_185/f_auto,q_auto/v1773307619/VantageFit/2026/Bijaya-Laxmi---Selfcare-cover_VFit_BLUE-copy.webp"
                                alt="Image for 111 Latest Employee Wellness Program Ideas & Examples (2024)"
                                width="189"
                                height="106"
                              />
                            </div>
                            <div className="col-12 col-md-7">
                              <h3 className="hm-blog-title">
                                <Trans>Top 15 Self Care Ideas to Practice at Work in 2024</Trans>
                              </h3>
                            </div>
                          </div>
                        </a>
                      </div>
                      {location.pathname === '/fr/' ? (
                        <div className="col-12 col-md-5 recent-articles">
                          <h3>
                            <Trans>Recent Articles</Trans>
                          </h3>

                          <a href={t(`/fr/blog/applications-de-defis-de-pas/`)}>
                            <div className="section-para">
                              <Trans>15 Meilleures Applications de défis de pas 2025</Trans>{' '}
                            </div>
                          </a>

                          <a href={t(`/fr/blog/jeux-du-vendredi-amusant/`)}>
                            <div className="section-para">
                              <Trans>Top 21 Jeux et Idées Fun Vendredi pour le Bien-Être des Employés</Trans>
                            </div>
                          </a>

                          <a href={t(`/fr/blog/mode-de-vie-sedentaire/`)}>
                            <div className="section-para">
                              <Trans>10 moyens efficaces de combattre un mode de vie sédentaire</Trans>
                            </div>
                          </a>

                          <a href={t(`/fr/blog/noms-dequipe-pour-defi-de-pas/`)}>
                            <div className="section-para">
                              <Trans>Noms d’Équipe pour Défi de Pas – 61 Idées Uniques</Trans>
                            </div>
                          </a>
                        </div>
                      ) : location.pathname === '/es/' ? (
                        <div className="col-12 col-md-5 recent-articles">
                          <h3>
                            <Trans>Recent Articles</Trans>
                          </h3>

                          <a href={t(`/es/blog/juegos-divertidos-para-los-viernes`)}>
                            <div className="section-para">
                              <Trans>
                                Los 21 mejores juegos y actividades de Viernes Divertido para el bienestar de los
                                empleados.
                              </Trans>
                            </div>
                          </a>

                          <a href={t(`/es/blog/estilo-de-vida-sedentario/`)}>
                            <div className="section-para">
                              <Trans>10 Formas Efectivas de Combatir un Estilo de Vida Sedentario</Trans>{' '}
                            </div>
                          </a>

                          <a href={t(`/es/blog/nombres-de-equipos-para-desafios-de-pasos/`)}>
                            <div className="section-para">
                              <Trans>61 Nombres Únicos para Equipos de Caminata para tu Próximo Desafío de Pasos</Trans>
                            </div>
                          </a>

                          <a href={t(`/es/blog/cultura-de-bienestar/`)}>
                            <div className="section-para">
                              <Trans>
                                Crear y fomentar una cultura de bienestar en el lugar de trabajo que realmente perdure
                              </Trans>
                            </div>
                          </a>
                        </div>
                      ) : location.pathname === '/de/' ? (
                        <div className="col-12 col-md-5 recent-articles">
                          <h3>
                            <Trans>Recent Articles</Trans>
                          </h3>

                          <a
                            href={t(
                              `/de/blog/top-21-fun-friday-spiele-und-ideen-fur-das-wohlbefinden-der-mitarbeiter/`
                            )}
                          >
                            <div className="section-para">
                              <Trans>Top 21 Fun-Friday-Spiele und Ideen für das Wohlbefinden der Mitarbeiter</Trans>{' '}
                            </div>
                          </a>

                          <a href={t(`/de/blog/sitzender-lebensstil/`)}>
                            <div className="section-para">
                              <Trans>10 wirksame Wege, um einen sitzenden Lebensstil zu bekämpfen</Trans>
                            </div>
                          </a>

                          <a href={t(`/de/blog/stress-am-arbeitsplatz/`)}>
                            <div className="section-para">
                              <Trans>Stress am Arbeitsplatz: Ein riesiges Thema für Unternehmen weltweit</Trans>
                            </div>
                          </a>

                          <a href={t(`/de/blog/wellness-programm-ziele-und-ziele/`)}>
                            <div className="section-para">
                              <Trans>Wellness-Programm Ziele und Ziele: Das Geheimnis des Erfolgs</Trans>
                            </div>
                          </a>
                        </div>
                      ) : (
                        <div className='col-12 col-md-5 '>
                          <Lazyload offset={200}>
                            <Suspense fallback={null}>
                              <RecentArticles />
                            </Suspense>
                          </Lazyload>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>
            <section className="bottom-feat-cta position-relative section-pdn-1 pt-8 feat_testi brad0">
              <div className="container custom_container d-flex justify-content-start align-items-center p0 m-d-block">
                <div className="col-12">
                  <h2 className="section-heading text-center no-br">
                    <Trans>
                      Unlock the power of a happier <br />& healthier workforce
                    </Trans>
                  </h2>
                  <div className="pl-0 col-12 mtb25 d-flex justify-content-center align-items-center">
                    <a href={t(`/request-demo/`)} className="vfitcompanycta arrowbtn-img">
                      <Trans>Book a Demo</Trans>
                    </a>
                  </div>
                </div>
              </div>
            </section>
          </div>
        ) : null}
      </Layout>
    </>
  )
}

export default Index

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(filter: { ns: { in: ["translation", "images"] }, language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`
